import React, { useEffect, useState, useRef } from 'react';

// Props:
// - value: external value (string)
// - onChange: fn(newValue) called debounced
// - placeholder: string
// - debounceMs: number (default 400)
// - storageKey: optional localStorage key to persist
// - className: optional wrapper classes
const SearchBox = ({
  value = '',
  onChange,
  placeholder = 'Search...',
  debounceMs = 400,
  storageKey,
  className = ''
}) => {
  const [input, setInput] = useState(value || '');
  const timer = useRef();
  const mounted = useRef(false);

  // Rehydrate from storage on mount if provided
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw !== null) {
        const saved = JSON.parse(raw);
        setInput(saved || '');
      }
    } catch {}
  }, [storageKey]);

  // Keep input synced with external value (e.g., resets)
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    setInput(value || '');
  }, [value]);

  // Debounce onChange and persist to storage
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (typeof onChange === 'function') onChange(input);
      if (storageKey && typeof window !== 'undefined') {
        try { window.localStorage.setItem(storageKey, JSON.stringify(input)); } catch {}
      }
    }, debounceMs);
    return () => timer.current && clearTimeout(timer.current);
  }, [input, onChange, debounceMs, storageKey]);

  return (
    <div className={className}>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={placeholder}
        className="w-full p-2"
      />
    </div>
  );
};

export default SearchBox;
